import { useCallback, useEffect, useMemo } from 'react';
import { AreaStack, AreaSeries as VisxAreaSeries } from '@visx/xychart';

import { useChartContext } from '../../../components/ChartContext';
import { DIM_OPACITY_FACTOR } from '../../../constants';
import type { ChartDataPoint, ChartDataPointValue } from '../../../types/ChartDataPoint';
import { remapYValueIfRight } from '../../../utils/scaleHelpers';
import type {
  AreaStackNullHandling,
  AreaStackSeriesConfig,
  AreaStackSeriesProps,
} from '../../types/AreaStackSeriesProps';
import type { YAxisSide } from '../../types/YAxisProps';
import { getStackLayerExtents } from '../../utils/stackLayerExtents';

import { StackLineMarkers } from './StackLineMarkers';

function resolveStackYAxis(yAxis?: YAxisSide): YAxisSide {
  return yAxis === 'right' ? 'right' : 'left';
}

type StackedAreaGroupProps = {
  series: AreaStackSeriesConfig[];
  yAxis: YAxisSide;
  fillOpacity: number;
  strokeWidth: number;
  nullHandling: AreaStackNullHandling;
  showStackLines: boolean;
};

function StackedAreaGroup({
  series,
  yAxis,
  fillOpacity,
  strokeWidth,
  nullHandling,
  showStackLines,
}: StackedAreaGroupProps) {
  const { visibleData, xDataKey, xScaleType, colorScale, chartClipPathId, yScaleConfig, yScaleRightConfig } =
    useChartContext();

  const getXValue = useCallback(
    (d: ChartDataPoint | undefined): ChartDataPointValue => {
      if (!d) {
        return null;
      }
      return d[xDataKey] ?? null;
    },
    [xDataKey],
  );

  const xAccessor = useCallback(
    (d: ChartDataPoint | undefined) => {
      const value = getXValue(d);
      if (value === null || value === undefined) {
        return xScaleType === 'band' ? '' : 0;
      }
      return xScaleType === 'band' ? String(value) : value;
    },
    [getXValue, xScaleType],
  );

  const stackedData = useMemo(() => {
    if (nullHandling === 'Zero') {
      const keys = series.map((s) => s.dataKey);
      return visibleData.map((datum) => {
        const result: ChartDataPoint = { ...datum };
        for (const key of keys) {
          const value = datum[key];
          if (value === null || value === undefined || (typeof value === 'number' && !Number.isFinite(value))) {
            result[key] = 0;
          }
        }
        return result;
      });
    }
    return visibleData;
  }, [visibleData, series, nullHandling]);

  const defaultYAccessor = useCallback((_d: ChartDataPoint | undefined) => 0, []);

  const yAccessorByKey = useMemo(() => {
    const map = new Map<string, (d: ChartDataPoint | undefined) => number>();
    for (const config of series) {
      const dataKey = config.dataKey;
      map.set(dataKey, (d: ChartDataPoint | undefined) => {
        if (!d) {
          return 0;
        }
        const value = d[dataKey];
        return typeof value === 'number' && Number.isFinite(value) ? value : 0;
      });
    }
    return map;
  }, [series]);

  // AreaStack adds each yAccessor, so remapping layers first is not remapping the
  // stacked total. Right-axis stacks plot remapped y0/y1 bounds instead.
  const remappedLayerAccessors = useMemo(() => {
    if (yAxis !== 'right') {
      return null;
    }
    const extentsByDatum = new WeakMap<ChartDataPoint, ReturnType<typeof getStackLayerExtents>>();
    const extentsFor = (d: ChartDataPoint | undefined) => {
      if (!d) {
        return getStackLayerExtents(undefined, series);
      }
      const cached = extentsByDatum.get(d);
      if (cached) {
        return cached;
      }
      const extents = getStackLayerExtents(d, series);
      extentsByDatum.set(d, extents);
      return extents;
    };
    return series.map((_, index) => ({
      y0Accessor: (d: ChartDataPoint | undefined) => {
        const layer = extentsFor(d)[index] ?? { y0: 0, y1: 0 };
        return remapYValueIfRight(layer.y0, 'right', yScaleRightConfig?.domain, yScaleConfig?.domain);
      },
      yAccessor: (d: ChartDataPoint | undefined) => {
        const layer = extentsFor(d)[index] ?? { y0: 0, y1: 0 };
        return remapYValueIfRight(layer.y1, 'right', yScaleRightConfig?.domain, yScaleConfig?.domain);
      },
    }));
  }, [series, yAxis, yScaleConfig?.domain, yScaleRightConfig?.domain]);

  const renderAreaSeries = (config: AreaStackSeriesConfig, index: number, remapped: boolean) => {
    const color = config.color ?? colorScale(config.seriesKey);
    const isDimmed = config.dimmed ?? false;
    const accessors = remapped ? remappedLayerAccessors?.[index] : undefined;
    return (
      <VisxAreaSeries
        key={config.seriesKey}
        dataKey={config.dataKey}
        data={stackedData}
        xAccessor={xAccessor}
        yAccessor={accessors?.yAccessor ?? yAccessorByKey.get(config.dataKey) ?? defaultYAccessor}
        {...(accessors ? { y0Accessor: accessors.y0Accessor } : {})}
        fill={color}
        fillOpacity={isDimmed ? fillOpacity * DIM_OPACITY_FACTOR : fillOpacity}
        renderLine
        lineProps={{
          stroke: color,
          strokeWidth,
          strokeOpacity: isDimmed ? DIM_OPACITY_FACTOR : 1,
          strokeLinejoin: 'round',
          strokeLinecap: 'round',
        }}
        clipPath={`url(#${chartClipPathId})`}
      />
    );
  };

  return (
    <>
      {yAxis === 'right' ? (
        series.map((config, index) => renderAreaSeries(config, index, true))
      ) : (
        <AreaStack>{series.map((config, index) => renderAreaSeries(config, index, false))}</AreaStack>
      )}
      {showStackLines && (
        <StackLineMarkers
          series={series}
          data={stackedData}
          xDataKey={xDataKey}
          xScaleType={xScaleType}
          colorScale={colorScale}
          yAxis={yAxis}
        />
      )}
    </>
  );
}

/**
 * AreaStackSeries component for composable LineChart.
 *
 * Renders multiple series as stacked areas using visx's AreaStack with
 * AreaSeries children. Registers each series with the tooltip system for hover display.
 * Series that share a `yAxis` stack together; left and right stacks are independent.
 *
 * @example
 * ```tsx
 * <LineChart data={data}>
 *   <AreaStackSeries
 *     series={[
 *       { seriesKey: 'cpu', name: 'CPU', dataKey: 'cpu', color: '#4d77ff' },
 *       { seriesKey: 'memory', name: 'Memory', dataKey: 'memory', color: '#00c853' },
 *     ]}
 *     fillOpacity={0.1}
 *     strokeWidth={2}
 *   />
 *   <Tooltip enabled />
 * </LineChart>
 * ```
 */
export function AreaStackSeries({
  series,
  fillOpacity = 0.1,
  strokeWidth = 2,
  nullHandling = 'Zero',
  showStackLines = true,
}: AreaStackSeriesProps) {
  const { colorScale, registerSeriesDisplayMetadata, unregisterSeriesDisplayMetadata } = useChartContext();

  const leftSeries = useMemo(() => series.filter((s) => resolveStackYAxis(s.yAxis) === 'left'), [series]);
  const rightSeries = useMemo(() => series.filter((s) => resolveStackYAxis(s.yAxis) === 'right'), [series]);

  useEffect(() => {
    for (const config of series) {
      if (config.showInTooltip === false) {
        continue;
      }
      const color = config.color ?? colorScale(config.seriesKey);
      registerSeriesDisplayMetadata(config.seriesKey, {
        name: config.name,
        color,
        dataKey: config.dataKey,
        tooltipRowIcon: config.tooltipRowIcon,
        yAxis: resolveStackYAxis(config.yAxis),
      });
    }
    return () => {
      for (const config of series) {
        if (config.showInTooltip === false) {
          continue;
        }
        unregisterSeriesDisplayMetadata(config.seriesKey);
      }
    };
  }, [series, colorScale, registerSeriesDisplayMetadata, unregisterSeriesDisplayMetadata]);

  if (series.length === 0) {
    return null;
  }

  return (
    <>
      {leftSeries.length > 0 && (
        <StackedAreaGroup
          series={leftSeries}
          yAxis="left"
          fillOpacity={fillOpacity}
          strokeWidth={strokeWidth}
          nullHandling={nullHandling}
          showStackLines={showStackLines}
        />
      )}
      {rightSeries.length > 0 && (
        <StackedAreaGroup
          series={rightSeries}
          yAxis="right"
          fillOpacity={fillOpacity}
          strokeWidth={strokeWidth}
          nullHandling={nullHandling}
          showStackLines={showStackLines}
        />
      )}
    </>
  );
}

AreaStackSeries.displayName = 'AreaStackSeries';
